import Head from 'expo-router/head';
import { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { type StyleProp, View as RNView, type ViewStyle } from 'react-native';
import {
    GRADIENT_COLOR_BOTTOM,
    GRADIENT_COLOR_TOP,
    isNonMobileWeb,
    isWeb,
} from 'smoothie';

import { BackgroundGradient } from './BackgroundGradient';

const MOBILE_WEB_HTML_GRADIENT = `linear-gradient(to bottom, ${GRADIENT_COLOR_TOP}, ${GRADIENT_COLOR_BOTTOM})`;

const OVERLAY_GRADIENT_COLOR_TOP = '#C6D7CF';
const OVERLAY_GRADIENT_COLOR_BOTTOM = '#A4BDC4';
const MOBILE_WEB_HTML_GRADIENT_WITH_OVERLAY = `linear-gradient(to bottom, ${OVERLAY_GRADIENT_COLOR_TOP}, ${OVERLAY_GRADIENT_COLOR_BOTTOM})`;

export const MOBILE_WEB_DEFAULT_VIEWPORT =
    'width=device-width, initial-scale=1, shrink-to-fit=no';

export const MOBILE_WEB_COVER_VIEWPORT =
    'width=device-width, initial-scale=1, shrink-to-fit=no, viewport-fit=cover';

const MOBILE_WEB_SCOPED_STYLE_ID = 'mobile-web-safari-gradient-background';

const MOBILE_WEB_SCOPED_CSS = `
html {
    background: ${MOBILE_WEB_HTML_GRADIENT};
    background-attachment: fixed;
    min-height: 100lvh;
}
body {
    background-color: transparent;
    min-height: 100lvh;
}
#root {
    background-color: transparent;
}
`;

// `position: 'fixed'` is valid for react-native-web at runtime (this
// component only renders on mobile web) but isn't in React Native's own
// ViewStyle type, hence the cast below.
const MOBILE_WEB_FIXED_GRADIENT_STYLE = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    width: '100%',
    zIndex: -1,
    pointerEvents: 'none',
} as unknown as StyleProp<ViewStyle>;

const syncPortaledGradientLayer = (
    gradientNode: HTMLElement | null | undefined,
): void => {
    if (gradientNode?.style == null) {
        return;
    }

    gradientNode.style.top = 'calc(-1 * env(safe-area-inset-top, 0px))';
    gradientNode.style.left = '0';
    gradientNode.style.right = '0';
    gradientNode.style.bottom = 'calc(-1 * env(safe-area-inset-bottom, 0px))';
    gradientNode.style.height =
        'calc(100lvh + env(safe-area-inset-top, 0px) + env(safe-area-inset-bottom, 0px))';
    gradientNode.style.minHeight = '100%';
};

/**
 * Extends the app gradient behind iOS Safari status and URL bars on mobile web.
 * Uses a portaled fixed layer plus a scoped html background while mounted.
 *
 * Document-level background-attachment: fixed is safe here because page scroll
 * happens inside nested ScrollViews, not on body/html.
 */
export const MobileWebSafariGradientBackground = ({
    overlayVisible = false,
}: {
    overlayVisible?: boolean;
} = {}) => {
    const isMobileWeb = isWeb && !isNonMobileWeb;
    const gradientLayerRef = useRef<RNView>(null);

    useLayoutEffect(() => {
        if (!isMobileWeb || typeof document === 'undefined') {
            return undefined;
        }

        const html = document.documentElement;
        const body = document.body;
        const previousHtmlBackground = html.style.background;
        const previousHtmlBackgroundAttachment =
            html.style.backgroundAttachment;
        const previousHtmlBackgroundColor = html.style.backgroundColor;
        const previousHtmlMinHeight = html.style.minHeight;
        const previousBodyBackgroundColor = body.style.backgroundColor;
        const previousBodyMinHeight = body.style.minHeight;

        html.style.background = MOBILE_WEB_HTML_GRADIENT;
        html.style.backgroundAttachment = 'fixed';
        html.style.backgroundColor = '';
        html.style.minHeight = '100lvh';
        body.style.backgroundColor = 'transparent';
        body.style.minHeight = '100lvh';

        let scopedStyleNode = document.getElementById(
            MOBILE_WEB_SCOPED_STYLE_ID,
        );
        const createdScopedStyleNode = scopedStyleNode == null;
        if (createdScopedStyleNode) {
            scopedStyleNode = document.createElement('style');
            scopedStyleNode.id = MOBILE_WEB_SCOPED_STYLE_ID;
            scopedStyleNode.textContent = MOBILE_WEB_SCOPED_CSS;
            document.head.appendChild(scopedStyleNode);
        }

        const viewportMetas = document.querySelectorAll(
            'meta[name="viewport"]',
        );
        const previousViewportContents = Array.from(viewportMetas).map(
            (meta) => meta.getAttribute('content') ?? '',
        );
        viewportMetas.forEach((meta) => {
            meta.setAttribute('content', MOBILE_WEB_COVER_VIEWPORT);
        });

        return () => {
            html.style.background = previousHtmlBackground;
            html.style.backgroundAttachment = previousHtmlBackgroundAttachment;
            html.style.backgroundColor = previousHtmlBackgroundColor;
            html.style.minHeight = previousHtmlMinHeight;
            body.style.backgroundColor = previousBodyBackgroundColor;
            body.style.minHeight = previousBodyMinHeight;
            if (createdScopedStyleNode && scopedStyleNode?.parentNode != null) {
                scopedStyleNode.parentNode.removeChild(scopedStyleNode);
            }
            viewportMetas.forEach((meta, index) => {
                meta.setAttribute(
                    'content',
                    previousViewportContents[index] ??
                        MOBILE_WEB_DEFAULT_VIEWPORT,
                );
            });
        };
    }, [isMobileWeb]);

    useLayoutEffect(() => {
        if (!isMobileWeb || typeof document === 'undefined') {
            return undefined;
        }

        const rootNode = document.getElementById('root');
        const previousRootBackground = rootNode?.style.backgroundColor ?? '';

        const syncGradientLayer = () => {
            syncPortaledGradientLayer(
                gradientLayerRef.current as unknown as HTMLElement | null,
            );
        };

        if (rootNode != null) {
            rootNode.style.backgroundColor = 'transparent';
        }

        syncGradientLayer();
        const rafId = requestAnimationFrame(syncGradientLayer);
        window.visualViewport?.addEventListener('resize', syncGradientLayer);

        return () => {
            cancelAnimationFrame(rafId);
            window.visualViewport?.removeEventListener(
                'resize',
                syncGradientLayer,
            );
            if (rootNode != null) {
                rootNode.style.backgroundColor = previousRootBackground;
            }
        };
    }, [isMobileWeb]);

    useLayoutEffect(() => {
        if (!isMobileWeb || typeof document === 'undefined') {
            return;
        }
        const html = document.documentElement;
        html.style.backgroundImage = overlayVisible
            ? MOBILE_WEB_HTML_GRADIENT_WITH_OVERLAY
            : MOBILE_WEB_HTML_GRADIENT;
    }, [isMobileWeb, overlayVisible]);

    if (!isMobileWeb) {
        return <BackgroundGradient />;
    }

    return (
        <>
            <Head>
                <meta
                    name="theme-color"
                    content={
                        overlayVisible
                            ? OVERLAY_GRADIENT_COLOR_TOP
                            : GRADIENT_COLOR_TOP
                    }
                />
            </Head>
            {typeof document !== 'undefined' &&
                createPortal(
                    <RNView
                        ref={gradientLayerRef}
                        style={MOBILE_WEB_FIXED_GRADIENT_STYLE}
                    >
                        <BackgroundGradient />
                    </RNView>,
                    document.body,
                )}
        </>
    );
};
